import { AppEntity } from '@app/icare/db/schema/common'
import { PatientOrderFormType } from '@app/icare/db/schema/form/PatientFormType'
import { ArticleCategoryId } from '@app/icare/entities/article/schema/ArticleCategory'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'

export const UnitOfMeasurement = {
  Piece: 'Piece',
  Box: 'Box',
  Carton: 'Carton',
  Couple: 'Couple',
}

export const ArticleStatusEnum = defineEnum({
  active: msg`Aktiv`,
  inactive: msg`Inaktiv`,
})

export type ArticleStatusType = typeof ArticleStatusEnum.$.Type

export const EconomicValueReasonTypeEnum = defineEnum({
  price: msg`Preis`,
  logistics: msg`Logistik`,
  availability: msg`Verfügbarkeit`,
  legal: msg`gesetzlich`,
  cost_carrier: msg`Kostenträger`,
  usability: msg`Verwendbarkeit`,
  other: msg`sonstiges`,
})

export type EconomicValueReasonType = typeof EconomicValueReasonTypeEnum.$.Type

export const EconomicValueReasonSchema = z.object({
  value: z.enum(EconomicValueReasonTypeEnum.$.zodEnum()),
  type: z.literal('select'),
})

export type EconomicValueReason = z.infer<typeof EconomicValueReasonSchema>

export const ArticleRelationDiscriminatorEnum = defineEnum({
  article: msg`Artikel`,
  article_category: msg`Artikelkategorie`,
})

export const ArticleRelationId = entityId('articleRelation')

export type ArticleRelationIdType = typeof ArticleRelationId.Type

export const ArticleId = entityId('article')

export type ArticleIdType = typeof ArticleId.Type

export const ArticleToArticleRelation = z.object({
  id: ArticleRelationId.schema,
  articleId: ArticleId.schema,
  type: z.literal('article').default('article'),
  toId: ArticleId.schema.nullable(),
  forms: z.array(z.enum(PatientOrderFormType)).default([]),
})

export type ArticleToArticleRelation = z.infer<typeof ArticleToArticleRelation>

export const ArticleToCategoryRelation = z.object({
  id: ArticleRelationId.schema,
  articleId: ArticleId.schema,
  type: z.literal('article_category').default('article_category'),
  toId: ArticleCategoryId.schema.nullable(),
  forms: z.array(z.enum(PatientOrderFormType)).default([]),
})

export type ArticleToCategoryRelationType = z.infer<typeof ArticleToCategoryRelation>

export const ArticleRelationSchema = z.discriminatedUnion('type', [ArticleToArticleRelation, ArticleToCategoryRelation])

export type ArticleRelation = z.infer<typeof ArticleRelationSchema>

const ArticleSource = {
  acriba: 'acriba',
  pharma: 'pharma',
} as const

export type ArticleSourceType = (typeof ArticleSource)[keyof typeof ArticleSource]

export const ArticleSchema = AppEntity.extend({
  id: ArticleId.schema,
  status: z.enum(ArticleStatusEnum.$.zodEnum()).default('active'),
  name: z.coerce.string().nullish(),
  pzn: z.coerce.string().nullish(),
  details: z
    .object({
      technicalName: z.string().nullish(),
      resourceNumber: z.string().nullish(),
      description: z.string().nullish(),
    })
    .nullish(),
  inventory: z
    .object({
      alwaysAvailable: z.boolean().default(false),
      available: z.boolean().default(true),
      onStock: z.coerce.number().default(0),
    })
    .nullish(),
  packaging: z
    .object({
      amount: z.coerce.number().nullish(),
      numberOfUnits: z.coerce.number().nullish(),
      unitOfMeasurement: z.coerce.string().nullish(),
      baseUnitOfMeasurement: z.coerce.string().nullish(),
    })
    .nullish(),
  pricing: z
    .object({
      pricePerUnit: z.number().nullish(),
      billingAccordingTo: z.coerce.string().nullish(),
    })
    .nullish(),
  economicValue: z
    .object({
      reason: z
        .array(
          z.object({
            value: z.enum(EconomicValueReasonTypeEnum.$.zodEnum()),
            type: z.literal('select'),
          }),
        )
        .default([]),
    })
    .nullish(),
  manufacturer: z
    .object({
      name: z.string(),
    })
    .nullish(),
  categoryIds: z.array(ArticleCategoryId.ref).default([]),
})

export type Article = z.infer<typeof ArticleSchema>
